/**
 * Options for a tooltip whose trigger exists *only* to show that tooltip — info
 * icons and the like. See `docs/exec-plans/11030-…` for the measurements behind
 * every value here.
 *
 * ## Why this is opt-in rather than a global theme override
 *
 * floating-vue's stock theme cannot be opened by tapping (#11030). It lists
 * `touch` as a trigger, but `hideTriggers` defaults to `(t) => [...t, "click"]`,
 * so `touch` lands in both lists — and it maps to `touchstart` for show,
 * `touchend` for hide. A tap schedules a show 200ms out and cancels it at once.
 *
 * Fixing that needs `click` as a show trigger. Doing it in the global theme would
 * reach all ~130 `v-tooltip` call sites, and 26 of those sit on buttons and links
 * where a tap must keep doing exactly one thing — it would fire the action *and*
 * pop a tooltip that (see `autoHide`) deliberately does not self-dismiss, leaving
 * one stranded over any modal the button opens.
 *
 * ## Why the two trigger sets differ
 *
 * Verified in real Chromium with real touch events, a tap delivers:
 *
 *   touchstart → touchend → mouseover → mouseenter → mousedown → mouseup
 *     → click → mouseout → mouseleave
 *
 * The browser releases its emulated hover *immediately after the click*, so any
 * set with `hover` in `hideTriggers` has its freshly-scheduled show cancelled by
 * that trailing `mouseleave`. `focus` is just as fatal where the icon sits inside
 * a `<label>`: the click hands focus to the label's control and the icon's `blur`
 * cancels the show. A mouse needs both to hide on mouse-out and tab-away, so one
 * set cannot serve both pointer types.
 *
 * The arrays are module constants so re-renders hand floating-vue the *same*
 * references — its `triggers` watcher is deep, and a fresh array every render
 * would tear down and re-add every listener.
 */
const MOUSE_TRIGGERS = ["hover", "focus", "click"];
const TOUCH_TRIGGERS = ["click"];

/**
 * Whether a tap should dismiss the tooltip — i.e. whether it landed outside the
 * trigger.
 *
 * `autoHide: true` cannot be used on touch: floating-vue registers document-level
 * `touchstart`/`touchend` handlers that reach `$_handleGlobalClose(event, true)`,
 * and the `true` sets `$_preventShow` for 300ms — swallowing the synthesised
 * `click` that follows the tap, so the tooltip could never open. As a predicate we
 * opt out when the tap is on the trigger, keeping tap-outside-to-dismiss without
 * blocking our own open.
 *
 * floating-vue invokes this as `popper.autoHide(event)`, so `this` is the popper.
 * `$_targetNodes` is private API: if a future version renames it we return
 * `false`, degrading to "outside tap does not dismiss" rather than the far worse
 * "tooltip can no longer be opened".
 */
function autoHideUnlessTapOnTrigger(this: unknown, event: Event): boolean {
  const targets = (this as { $_targetNodes?: Element[] })?.$_targetNodes;
  if (!targets || !(event?.target instanceof Node)) return false;
  return !targets.some((node) => node.contains(event.target as Node));
}

const POINTER_QUERY = "(hover: none)";

let coarsePointer: ReturnType<typeof useMediaQuery> | undefined;

/**
 * Whether the device reports a coarse pointer, tracked live.
 *
 * Created on first use rather than at module load so that reading it during render
 * makes consumers re-render when it flips, which patches the directive's options
 * and lets floating-vue's own `triggers` watcher re-register the listeners. Read it
 * once up front instead and behaviour freezes at page load — a view opened in
 * mobile emulation kept the touch triggers after a switch to desktop, so hover
 * stayed dead until a reload.
 *
 * The **detached** scope matters as much as the laziness. `useMediaQuery` releases
 * its `change` listener through `tryOnScopeDispose`, which binds to whatever scope
 * is active when it is called. Created inline, that would be whichever component
 * happened to read this first — and its unmount would then silently stop pointer
 * tracking for the entire app, with this cached ref frozen at its last value.
 * Verified: first read inside a component's `setup`, then unmounting it, left the
 * triggers stale. A detached scope has no owner to be torn down by.
 */
function isCoarsePointer(): boolean {
  coarsePointer ??= effectScope(true).run(() => useMediaQuery(POINTER_QUERY));
  return coarsePointer?.value ?? false;
}

export type InfoTooltipOptions = {
  content: string;
  triggers: string[];
  hideTriggers: string[];
  noAutoFocus: true;
  autoHide: true | typeof autoHideUnlessTapOnTrigger;
  /** Extra floating-vue options a call site passes through, e.g. `popperClass`. */
  [option: string]: unknown;
};

/**
 * Build `v-tooltip` options for an info-icon-style trigger.
 *
 * Read the pointer type during render — bind a `computed` from `<script setup>`,
 * not an inline template call — so the read is a tracked dependency. (Inline also
 * fails to type-check: auto-imported helpers don't resolve in template
 * expressions.)
 *
 * `extra` covers per-site floating-vue options such as `placement`, `html` or
 * `popperClass`. It is spread *before* the trigger config so a call site cannot
 * accidentally reintroduce the tap-cancelling combinations documented above.
 */
export function infoTooltipOptions(
  content: string | undefined,
  extra?: Record<string, unknown>
): InfoTooltipOptions {
  const touch = isCoarsePointer();

  return {
    ...extra,
    content: content ?? "",
    triggers: touch ? TOUCH_TRIGGERS : MOUSE_TRIGGERS,
    hideTriggers: touch ? TOUCH_TRIGGERS : MOUSE_TRIGGERS,
    // Mandatory wherever `autoHide` is truthy: `$_applyShow` ends with
    // `this.noAutoFocus || this.$_popperNode.focus()`, so without it a plain hover
    // pulls focus out of whatever input you were typing in. Measured, not assumed.
    noAutoFocus: true,
    autoHide: touch ? autoHideUnlessTapOnTrigger : true,
  };
}
